/**
 * formRules — LUẬT CỦA FORM trong DataListManager: field tạm, bắt buộc theo điều kiện, và DỰNG PAYLOAD LƯU.
 *
 * Bù ba khoảng trống so với InputBase:
 *  - `is_tmp`: field chỉ để tính toán trên form, KHÔNG được ghi xuống DB.
 *  - `required` dạng script: "có ngoại tệ thì bắt buộc tỷ giá".
 *  - Payload lưu: DataListManager trước đây gửi lại NGUYÊN bản ghi đã đọc lúc mở form
 *    (`DataListForm` nhân bản sâu `initialData`), nên nếu người khác duyệt/sửa trong lúc form đang mở
 *    thì lần lưu sẽ ghi đè bằng dữ liệu cũ. InputBase tránh bằng cách chỉ gửi PHẦN ĐỔI
 *    (`InputBase.js:2331` difference(data, old_data)) và né `approve_data`.
 *
 * File KHÔNG import gì để test chạy được trong node trên chính mã nguồn này.
 */

// Field do SERVER quản lý — client không bao giờ được gửi lên (gửi lên là ghi đè kết quả của người khác).
const SERVER_MANAGED_FIELDS = [
	'approve_data', 'date_created', 'date_updated', 'user_created', 'user_updated',
	'__v', 'is_full_data', 'collection_name',
];

/** So sánh sâu, đủ dùng cho dữ liệu JSON của form (không có Date/RegExp lồng sâu bất thường). */
function deepEqual(a, b) {
	if (a === b) return true;
	if (a === null || b === null || a === undefined || b === undefined) return false;
	// Ngày phải xét TRƯỚC khi so kiểu: sau khi lưu, server trả ngày dạng chuỗi ISO trong khi form đang giữ
	// đối tượng Date -> nếu coi là "đã đổi" thì lần lưu sau gửi thừa, mà coi nhầm là bằng thì mất dữ liệu.
	if (a instanceof Date || b instanceof Date) {
		const ta = a instanceof Date ? a.getTime() : new Date(a).getTime();
		const tb = b instanceof Date ? b.getTime() : new Date(b).getTime();
		return !Number.isNaN(ta) && !Number.isNaN(tb) && ta === tb;
	}
	if (typeof a !== typeof b) return false;
	if (typeof a !== 'object') return false;
	const aIsArr = Array.isArray(a), bIsArr = Array.isArray(b);
	if (aIsArr !== bIsArr) return false;
	if (aIsArr) {
		if (a.length !== b.length) return false;
		return a.every((v, i) => deepEqual(v, b[i]));
	}
	const ka = Object.keys(a), kb = Object.keys(b);
	if (ka.length !== kb.length) return false;
	return ka.every(k => Object.prototype.hasOwnProperty.call(b, k) && deepEqual(a[k], b[k]));
}

/** Tên các field TẠM (isTemp) — kể cả field lồng trong ObjectArray thì cũng chỉ xét cấp ngoài cùng. */
function tempFieldNames(fields) {
	return (fields || []).filter(f => f && f.isTemp === true && f.field).map(f => f.field);
}

/**
 * `required` có thể là Boolean hoặc hàm (context, formData, masterData, fields) -> Boolean.
 * Hàm ném lỗi thì coi như KHÔNG bắt buộc (đừng chặn người dùng lưu vì một script hỏng).
 */
function resolveRequired(field, args) {
	const r = field && field.required;
	// Chuỗi = script CHƯA được biên dịch (cấu hình nạp không qua parseDataListConfig). Coi là KHÔNG bắt
	// buộc: `!!"return false"` là true, tức mọi field có script sẽ hoá bắt buộc và người dùng không lưu nổi.
	if (typeof r === 'string') return false;
	if (typeof r === 'function') {
		try { return !!r(args && args.context, args && args.formData, args && args.masterData, args && args.fields); }
		catch (e) { return false; }
	}
	return !!r;
}

/** Kết quả của checkBeforeSave -> thông báo chặn lưu, hoặc null nếu cho lưu. */
function interpretCheckResult(rs) {
	if (!rs) return null;
	if (typeof rs === 'string') return rs.trim() ? rs : null;
	if (typeof rs === 'object') {
		const msg = rs.error || rs.message;
		if (typeof msg === 'string' && msg.trim()) return msg;
		if (msg) return String(msg);
	}
	return null;
}

/**
 * Dựng dữ liệu gửi lên server.
 *  - THÊM MỚI: gửi cả form (trừ field tạm và field do server quản lý).
 *  - SỬA: chỉ gửi `_id` + những khoá THẬT SỰ đổi so với lúc mở form. Nhờ vậy hai người sửa hai phần khác
 *    nhau của cùng một chứng từ không ghi đè lẫn nhau, và `approve_data` không bao giờ bị đẩy ngược.
 * Giá trị kiểu object/mảng có đổi thì gửi NGUYÊN giá trị mới (không gửi diff) — giống InputBase, vì server
 * thay cả nhánh chứ không merge từng khoá.
 */
function buildSavePayload(formData, initialData, fields, options) {
	const opts = options || {};
	const temps = tempFieldNames(fields);
	const blocked = new Set([...SERVER_MANAGED_FIELDS, ...temps, ...(opts.exclude || [])]);
	const src = formData || {};
	const isEditing = !!(initialData && initialData._id && src._id && String(initialData._id) === String(src._id));

	if (!isEditing) {
		const out = {};
		for (const key of Object.keys(src)) {
			if (blocked.has(key)) continue;
			if (src[key] === undefined) continue;
			out[key] = src[key];
		}
		return out;
	}

	const out = { _id: src._id };
	for (const key of Object.keys(src)) {
		if (key === '_id' || blocked.has(key)) continue;
		if (src[key] === undefined) continue;
		if (!deepEqual(src[key], initialData[key])) out[key] = src[key];
	}
	// Khoá định danh luôn đi kèm để server định tuyến đúng loại chứng từ (nếu form có).
	for (const key of (opts.alwaysKeep || ['id_app', 'ma_ct'])) {
		if (src[key] !== undefined && out[key] === undefined) out[key] = src[key];
	}
	return out;
}

/**
 * ÉP KIỂU giá trị ngay tại CỬA VÀO của form — đúng chỗ bộ cũ làm
 * (`GroupTabs.js:1259`: `handleValueChanged(field, { [field.name]: Number(event.target.value) })`).
 *
 * Ô nhập số của bộ mới đi qua `react-number-format`, mà `values.value` của thư viện đó là một **CHUỖI**
 * (`floatValue` mới là số). Hậu quả không nhìn thấy ngay nhưng sai nặng:
 *   - script `handle_value_changed` cộng bằng `+` thì thành NỐI CHUỖI: `4 + "5"` ra `"45"`, rồi con số
 *     đó được gửi thẳng vào điều kiện gọi API lấy giá -> lấy về giá của một số lượng khác hẳn;
 *   - bản ghi lưu xuống server mang chuỗi ở field số.
 *
 * KHÁC bộ cũ có chủ ý: `Number('')` ra `0`, tức xoá trắng ô số thì bộ cũ ghi `0` — `required` cũng
 * "đạt" luôn. Ở đây rỗng thì GIỮ RỖNG, và chuỗi không đọc được thành số thì giữ nguyên văn để người
 * dùng còn sửa (ép ra `NaN` là mất trắng cái họ vừa gõ).
 */
function coerceFieldValue(field, value) {
	if (!field || field.type !== 'Number') return value;
	if (value === '' || value === null || value === undefined) return value;
	if (typeof value === 'number') return value;
	const n = Number(value);
	return isNaN(n) ? value : n;
}

export {
	SERVER_MANAGED_FIELDS, deepEqual, tempFieldNames, resolveRequired,
	interpretCheckResult, buildSavePayload, coerceFieldValue,
};
